import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  FiHome,
  FiUser,
  FiSearch,
  FiMenu,
  FiBell,
  FiLogOut,
  FiInfo,
  FiSun,
  FiMoon
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { get } from "../../services/ApiService";
import { persistUserIdentity, clearStoredIdentity, readStoredUserId } from "../../services/sessionIdentity";
import "./Header.css";

const MAX_RECENT = 20;
const POLL_MS = 12000;
const GLOBAL_VERSION_INDEX_KEY = "buglog:globalVersionIndex";
const THEME_KEY = "buglog-theme";
const THEME_COLORS = {
  light: "#489BF4",
  dark: "#0d1117",
};

const VERSION_UID_PREFIX = "BL-";
const VERSION_ID_MIN_LEN = 6;

const LOCAL_NOTIFS_KEY = "buglog:localNotifs";
const MAX_LOCAL_NOTIFS = 60;

const safeParse = (v) => {
  try {
    if (!v) return null;
    return JSON.parse(v);
  } catch {
    return null;
  }
};

const getUidDigits = (value) => {
  const t = String(value || "").trim().toUpperCase();
  if (!t) return "";
  if (t.startsWith(VERSION_UID_PREFIX)) return t.slice(VERSION_UID_PREFIX.length).replace(/\D/g, "");
  return t.replace(/\D/g, "");
};

const normalizeVersionUid = (value) => {
  const digits = getUidDigits(value);
  if (!digits) return "";
  return `${VERSION_UID_PREFIX}${digits}`;
};

const normalizeVersionDigits = (value) => {
  const digits = getUidDigits(value);
  return digits || "";
};

const isVersionUid = (value) => {
  const uid = normalizeVersionUid(value);
  if (!uid) return false;
  const m = uid.match(/^BL-(\d+)$/);
  if (!m) return false;
  return m[1].length >= VERSION_ID_MIN_LEN;
};

const isVersionDigits = (value) => {
  const id = normalizeVersionDigits(value);
  return /^\d+$/.test(id) && id.length >= VERSION_ID_MIN_LEN;
};

const toMs = (val) => {
  if (typeof val === "number") return val;
  const ms = Date.parse(String(val || ""));
  return Number.isFinite(ms) ? ms : 0;
};

const toBool = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "yes";

const normalizeId = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const canManageProjectRecord = (project) => {
  const currentUserId = readStoredUserId();
  const creatorId = normalizeId(
    project?.created_by ??
      project?.created_by_id ??
      project?.owner_id ??
      project?.user_id
  );
  const rawRole = String(
    project?.role ??
      project?.member_role ??
      project?.project_role ??
      project?.permission ??
      ""
  )
    .trim()
    .toLowerCase();

  if (
    toBool(project?.is_owner) ||
    toBool(project?.owner) ||
    toBool(project?.is_admin) ||
    toBool(project?.admin) ||
    toBool(project?.is_manager) ||
    toBool(project?.manager)
  ) {
    return true;
  }

  if (["owner", "admin", "administrator", "manager", "yönetici", "yonetici"].includes(rawRole)) {
    return true;
  }

  return !!(currentUserId && creatorId && currentUserId === creatorId);
};

const readLocalNotifs = () => {
  try {
    const raw = localStorage.getItem(LOCAL_NOTIFS_KEY);
    const parsed = safeParse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeLocalNotifs = (arr) => {
  try {
    localStorage.setItem(LOCAL_NOTIFS_KEY, JSON.stringify(arr || []));
  } catch {
  }
};

const updateThemeMetaColor = (theme) => {
  try {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", THEME_COLORS[theme] || THEME_COLORS.light);
  } catch {
  }
};

const mergeNotifs = (locals, server) => {
  const all = [...(Array.isArray(locals) ? locals : []), ...(Array.isArray(server) ? server : [])]
    .filter((n) => n && n.id != null)
    .sort((a, b) => toMs(b.created_at) - toMs(a.created_at));

  const seen = new Set();
  const out = [];

  for (const n of all) {
    const key = String(n.id);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(n);
  }
  return out;
};

const readGlobalVersionIndex = () => {
  try {
    const raw = localStorage.getItem(GLOBAL_VERSION_INDEX_KEY);
    const parsed = safeParse(raw);

    const base =
      parsed && typeof parsed === "object"
        ? parsed
        : { byVersionKey: {}, byId: {} };

    const byId = base.byId && typeof base.byId === "object" ? base.byId : {};
    return { byId };
  } catch {
    return { byId: {} };
  }
};

const putJson = async (url, data) => {
  try {
    const res = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include"
    });
    return await res.json();
  } catch (e) {
    return { status: "error", error: e?.message || "PUT failed" };
  }
};

const buildBugHrefFromPath = (path, bugId) => {
  const base = String(path || "").split("?")[0];
  if (!base) return "/";
  if (!bugId) return base;
  const qs = new URLSearchParams();
  qs.set("bug", String(bugId));
  return `${base}?${qs.toString()}`;
};

const buildBugHref = (d) => {
  const bugId = d?.bug_id || d?.bugId || d?.bug || d?.id || "";
  const bid = String(bugId || "").trim();

  const rawHref = d?.href ? String(d.href) : "";
  if (rawHref) {
    if (bid && !rawHref.includes("bug=")) {
      const sep = rawHref.includes("?") ? "&" : "?";
      return `${rawHref}${sep}bug=${encodeURIComponent(bid)}`;
    }
    return rawHref;
  }

  const pu = d?.projectUniq || d?.uniq || "";
  const pid = d?.projectId || d?.project_id || "";
  const pn = d?.projectName || d?.project_name || "";

  if (pu && pid && pn) {
    return buildBugHrefFromPath(`/bugs/${pu}/${pid}/${pn}`, bid);
  }

  const currentPath = String(window.location.pathname || "");
  if (currentPath.startsWith("/bugs/")) {
    return buildBugHrefFromPath(currentPath, bid);
  }

  return bid ? `/?bug=${encodeURIComponent(bid)}` : "/";
};

const mapNotification = (n) => {
  const isRead =
    n?.is_read === 1 ||
    n?.is_read === "1" ||
    n?.is_read === true ||
    n?.is_read === "true";

  const bugId = n?.bug_id || n?.bugId || n?.target_bug_id || n?.bug || null;

  const fallbackHref =
    n?.projectUniq && n?.projectId && n?.projectName
      ? buildBugHrefFromPath(`/bugs/${n.projectUniq}/${n.projectId}/${n.projectName}`, bugId || n?.id || "")
      : "/";

  const href = n?.href ? buildBugHref({ href: n.href, id: bugId || n?.id || "" }) : fallbackHref;

  return {
    id: n?.id ?? `${n?.created_at || Date.now()}`,
    title: n?.title || n?.message || "Yeni iş",
    projectName: n?.project_name || n?.projectName || "",
    href,
    created_at: n?.created_at || n?.ts || Date.now(),
    is_read: !!isRead,
    actor_user_id: String(
      n?.actor_user_id ??
      n?.actorUserId ??
      n?.created_by ??
      n?.user_id ??
      ""
    ).trim()
  };
};

const Header = () => {
  const nav = useNavigate();

  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const itemRefs = useRef([]);
  const searchInputRef = useRef(null);

  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const timer = useRef();

  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const notifRef = useRef(null);
  const [notifOpen, setNotifOpen] = useState(false);

  const [isDark, setIsDark] = useState(
    () => {
      try {
        return document.documentElement.classList.contains("dark");
      } catch {
        return false;
      }
    }
  );

  const toggleTheme = () => {
    const root = document.documentElement;
    const next = !root.classList.contains("dark");
    const theme = next ? "dark" : "light";

    root.classList.add("theme-switching");
    root.classList.toggle("dark", next);
    root.dataset.theme = theme;
    setIsDark(next);
    updateThemeMetaColor(theme);

    try {
      localStorage.setItem(THEME_KEY, theme);
      window.dispatchEvent(new CustomEvent("buglog:themechange", { detail: { theme } }));
    } catch {
    }

    setTimeout(() => root.classList.remove("theme-switching"), 400);
  };

  useEffect(() => {
    const root = document.documentElement;
    const syncTheme = () => {
      const next = root.classList.contains("dark");
      setIsDark(next);
      updateThemeMetaColor(next ? "dark" : "light");
    };

    syncTheme();

    const observer = new MutationObserver(syncTheme);
    observer.observe(root, { attributes: true, attributeFilter: ["class", "data-theme"] });

    const onStorage = (e) => {
      if (e.key === THEME_KEY) syncTheme();
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("buglog:themechange", syncTheme);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("buglog:themechange", syncTheme);
    };
  }, []);

  const [notifications, setNotifications] = useState(() => {
    const locals = readLocalNotifs();
    return mergeNotifs(locals, []);
  });
  const [unread, setUnread] = useState(() => {
    const locals = readLocalNotifs();
    return (locals || []).reduce((acc, n) => acc + (n?.is_read ? 0 : 1), 0);
  });

  const pollRef = useRef(null);
  const userIdRef = useRef(null);

  const [recentBugs, setRecentBugs] = useState(() => {
    try {
      const raw = localStorage.getItem("recentBugs");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const uid = readStoredUserId() || null;
    userIdRef.current = uid ? String(uid) : null;
  }, []);

  useEffect(() => {
    let alive = true;

    const syncCurrentUser = async () => {
      if (userIdRef.current || !Cookies.get("token")) return;

      try {
        const res = await get("/user/profile");
        const user = res?.user || null;
        if (!alive || !user) return;
        persistUserIdentity(user);
        const uid = readStoredUserId();
        userIdRef.current = uid || null;
      } catch {
      }
    };

    syncCurrentUser();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const onGlobalShortcut = (e) => {
      const key = String(e.key || "").toLowerCase();
      const isMac = /mac|iphone|ipad|ipod/i.test(navigator.platform || "");
      const mod = isMac ? e.metaKey : e.ctrlKey;

      if (!mod) return;
      if (key !== "p" && key !== "f") return;

      e.preventDefault();
      e.stopPropagation();

      setMenuOpen(false);
      setNotifOpen(false);

      setOpen(true);

      try {
        const el = searchInputRef.current;
        if (el) {
          el.focus();
          el.select();
        }
      } catch {
      }
    };

    window.addEventListener("keydown", onGlobalShortcut, true);
    return () => window.removeEventListener("keydown", onGlobalShortcut, true);
  }, []);

  const goVersion = (rec) => {
    try {
      const base = String(rec?.path || "").split("?")[0];
      const digits = normalizeVersionDigits(rec?.uid || rec?.id || "");
      if (!base || !isVersionDigits(digits)) return;

      const qs = new URLSearchParams();
      qs.set("vid", digits);
      if (rec?.versionName) qs.set("ver", String(rec.versionName));

      setOpen(false);
      setQ("");
      setActiveIdx(-1);

      nav(`${base}?${qs.toString()}`);
    } catch {
    }
  };

  const resolveVersionByUid = (raw) => {
    const uid = normalizeVersionUid(raw);
    const digits = normalizeVersionDigits(raw);

    if (!isVersionUid(uid) || !isVersionDigits(digits)) return null;

    const idx = readGlobalVersionIndex();

    const rec = idx.byId?.[uid] || idx.byId?.[digits] || null;
    if (!rec) return null;

    return {
      kind: "version",
      id: uid,
      uid,
      versionName: rec.versionName || "",
      projectName: rec.projectName || "",
      path: rec.path || ""
    };
  };

  useEffect(() => {
    clearTimeout(timer.current);

    const trimmed = String(q || "").trim();
    if (!trimmed) {
      setList([]);
      setOpen(false);
      setActiveIdx(-1);
      return;
    }

    const uidCandidate = normalizeVersionUid(trimmed);
    const digitsCandidate = normalizeVersionDigits(trimmed);

    if (isVersionUid(uidCandidate) || isVersionDigits(digitsCandidate)) {
      const found = resolveVersionByUid(trimmed);
      if (found) {
        setList([found]);
        setOpen(true);
        setActiveIdx(0);
      } else {
        setList([]);
        setOpen(true);
        setActiveIdx(-1);
      }
      return;
    }

    timer.current = setTimeout(async () => {
      const res = await get("/projects", { q: trimmed });
      if (res.status === "success") {
        setList(res.projects || []);
        setOpen(true);
        setActiveIdx(-1);
      }
    }, 350);
  }, [q]);

  useEffect(() => {
    const h = (e) => {
      if (open && wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
      if (notifOpen && notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open, menuOpen, notifOpen]);

  useEffect(() => {
    if (activeIdx < 0) return;
    const el = itemRefs.current[activeIdx];
    if (el && listRef.current) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx]);

  const goProject = (p) => {
    setOpen(false);
    setQ("");
    setActiveIdx(-1);
    nav(`/bugs/${p.uniq}/${canManageProjectRecord(p) ? 1 : 0}/${encodeURIComponent(p.name || "Proje")}`);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => {
        const next = Math.min((i < 0 ? -1 : i) + 1, list.length - 1);
        return list.length ? next : -1;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => {
        const next = Math.max((i < 0 ? 0 : i) - 1, 0);
        return list.length ? next : -1;
      });
    } else if (e.key === "Enter") {
      const found = resolveVersionByUid(q);
      if (found) {
        e.preventDefault();
        goVersion(found);
        return;
      }

      if (open && activeIdx >= 0 && list[activeIdx]) {
        e.preventDefault();
        const item = list[activeIdx];
        if (item?.kind === "version") {
          goVersion(item);
        } else {
          goProject(item);
        }
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIdx(-1);
    }
  };

  useEffect(() => {
    if ("Notification" in window) {
      if (Notification.permission === "default") {
        try {
          Notification.requestPermission();
        } catch {
        }
      }
    }

    const addRecentLocal = (d) => {
      const bugId = d?.bug_id || d?.bugId || d?.id || `${Date.now()}`;
      const href = buildBugHref({ ...d, id: bugId });
      const item = {
        id: bugId,
        title: d.title || "Yeni iş",
        projectName: d.projectName || d.project_name || "",
        href,
        ts: Date.now()
      };
      setRecentBugs((prev) => {
        const arr = [item, ...prev].slice(0, MAX_RECENT);
        try {
          localStorage.setItem("recentBugs", JSON.stringify(arr));
        } catch {
        }
        return arr;
      });
    };

    const addToNotifications = (d) => {
      const bugId = d?.bug_id || d?.bugId || d?.id || "";
      const href = buildBugHref({ ...d, id: bugId });

      const localNotif = {
        id: `local-${String(bugId || Date.now())}-${Date.now()}`,
        title: d?.title ? `Yeni İş: ${d.title}` : "Yeni iş",
        projectName: d?.projectName || d?.project_name || "",
        href,
        created_at: Date.now(),
        is_read: false
      };

      const locals = readLocalNotifs();
      const nextLocals = [localNotif, ...locals].slice(0, MAX_LOCAL_NOTIFS);
      writeLocalNotifs(nextLocals);

      setNotifications((prev) => {
        const merged = mergeNotifs(nextLocals, prev || []);
        const unreadCount = merged.reduce((acc, n) => acc + (n?.is_read ? 0 : 1), 0);
        setUnread(unreadCount);
        return merged;
      });
    };

    const onBugCreated = (e) => {
      const d = e?.detail || {};
      addRecentLocal(d);

      const myId = userIdRef.current ? String(userIdRef.current) : "";
      const createdBy = d?.created_by != null ? String(d.created_by) : "";

      if (myId && createdBy && myId === createdBy) {
        return;
      }

      addToNotifications(d);

      if (!("Notification" in window)) return;
      const title = `Yeni İş: ${d.title || "Yeni kayıt"}`;
      const body = d.projectName
        ? `${d.projectName} içinde oluşturuldu.`
        : "Yeni bir iş oluşturuldu.";

      if (Notification.permission === "granted") {
        try {
          const n = new Notification(title, {
            body,
            tag: `bug-${d.id || Date.now()}`
          });
          n.onclick = () => {
            try {
              window.focus();
            } catch {
            }
            const href = buildBugHref(d);
            nav(href || "/");
          };
        } catch {
        }
      }
    };

    window.addEventListener("bug:created", onBugCreated);
    return () => window.removeEventListener("bug:created", onBugCreated);
  }, [nav]);

  const fetchNotifications = useMemo(() => {
    return async () => {
      const uid = userIdRef.current;
      const locals = readLocalNotifs();

      if (!uid) {
        const mergedOnlyLocal = mergeNotifs(locals, []);
        setNotifications(mergedOnlyLocal);
        setUnread(mergedOnlyLocal.reduce((acc, n) => acc + (n?.is_read ? 0 : 1), 0));
        return;
      }

      try {
        const res = await get("/notifications.php", { user_id: uid });
        if (res?.status === "success" && Array.isArray(res.notifications)) {
          const mapped = res.notifications
            .map(mapNotification)
            .filter((item) => {
              const actorId = String(item?.actor_user_id || "").trim();
              return !(uid && actorId && uid === actorId);
            });
          const combined = mergeNotifs(locals, mapped);
          setNotifications(combined);
          setUnread(combined.reduce((acc, n) => acc + (n?.is_read ? 0 : 1), 0));
        } else {
          const combined = mergeNotifs(locals, []);
          setNotifications(combined);
          setUnread(combined.reduce((acc, n) => acc + (n?.is_read ? 0 : 1), 0));
        }
      } catch {
        const combined = mergeNotifs(locals, []);
        setNotifications(combined);
        setUnread(combined.reduce((acc, n) => acc + (n?.is_read ? 0 : 1), 0));
      }
    };
  }, []);

  useEffect(() => {
    fetchNotifications();
    pollRef.current = setInterval(fetchNotifications, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchNotifications]);

  const openNotif = () => {
    setNotifOpen((o) => !o);
    if (!notifOpen) {
      setUnread(0);
    }
  };

  const goBug = async (href, n) => {
    const nid = String(n?.id || "");
    const isLocal = nid.startsWith("local-");

    if (isLocal) {
      setNotifications((prev) => {
        const next = (prev || []).map((x) =>
          String(x.id) === nid ? { ...x, is_read: true } : x
        );
        const localsOnly = next.filter((x) => String(x.id).startsWith("local-")).slice(0, MAX_LOCAL_NOTIFS);
        writeLocalNotifs(localsOnly);
        const unreadCount = next.reduce((acc, item) => acc + (item?.is_read ? 0 : 1), 0);
        setUnread(unreadCount);
        return next;
      });

      setNotifOpen(false);
      nav(href || "/");
      return;
    }

    if (n?.id) {
      try {
        await putJson("/notifications.php", { notification_id: n.id });
        setNotifications((prev) => {
          const next = (prev || []).map((x) =>
            x.id === n.id ? { ...x, is_read: true } : x
          );
          const unreadCount = next.reduce((acc, item) => acc + (item?.is_read ? 0 : 1), 0);
          setUnread(unreadCount);
          return next;
        });
      } catch {
      }
    }

    setNotifOpen(false);
    nav(href || "/");
  };

  const clearRecent = async () => {
    const unreadServerItems = notifications.filter(
      (n) => !n.is_read && !String(n.id).startsWith("local-")
    );

    if (unreadServerItems.length) {
      try {
        await Promise.all(
          unreadServerItems.map((n) => putJson("/notifications.php", { notification_id: n.id }))
        );
      } catch {
      }
    }

    setNotifications((prev) => {
      const next = (prev || []).map((n) => ({ ...n, is_read: true }));
      const localsOnly = next.filter((x) => String(x.id).startsWith("local-")).slice(0, MAX_LOCAL_NOTIFS);
      writeLocalNotifs(localsOnly);
      return next;
    });

    setUnread(0);
  };

  const handleLogout = () => {
    try {
      clearStoredIdentity();
      Cookies.remove("token");
    } catch {
    }
    nav("/");
    window.location.reload();
  };

  return (
    <header className="header">
      <div className="header-inner">
        <button className="logo-btn" onClick={() => nav("/")} type="button">
          <img src="/buglog.svg" alt="Buglog" className="logo" />
        </button>

        <div className="search-wrap" ref={wrapRef}>
          <FiSearch size={18} />
          <input
            ref={searchInputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Projelerde ara..."
            aria-autocomplete="list"
            aria-expanded={open}
            aria-activedescendant={
              activeIdx >= 0 && list[activeIdx] ? `sr-${list[activeIdx].id}` : undefined
            }
          />
          {open && (
            <ul className="search-results" ref={listRef} role="listbox">
              {list.length ? (
                list.map((p, i) => {
                  const isVer = p?.kind === "version";
                  const title = isVer
                    ? `${String(p.projectName || "")} · ${String(p.versionName || "")} (${String(p.uid || "")})`
                    : String(p.name || "");
                  const logoText = (isVer ? String(p.projectName || "") : String(p.name || ""))
                    .split(" ")
                    .slice(0, 2)
                    .map((w) => w[0])
                    .join("")
                    .toUpperCase();

                  return (
                    <li
                      id={`sr-${p.id}`}
                      key={p.id}
                      ref={(el) => (itemRefs.current[i] = el)}
                      className={i === activeIdx ? "active" : ""}
                      onMouseEnter={() => setActiveIdx(i)}
                      onMouseLeave={() => setActiveIdx(-1)}
                      onClick={() => {
                        if (isVer) {
                          goVersion(p);
                        } else {
                          goProject(p);
                        }
                      }}
                      role="option"
                      aria-selected={i === activeIdx}
                    >
                      <span className="sr-logo">{logoText}</span>
                      <span className="sr-name">{title}</span>
                    </li>
                  );
                })
              ) : (
                <li className="sr-empty">Sonuç yok</li>
              )}
            </ul>
          )}
        </div>

        <div className="header-actions">
          <div className="notif-wrap" ref={notifRef}>
            <button
              className="notif-btn"
              aria-haspopup="menu"
              aria-expanded={notifOpen}
              onClick={openNotif}
              title="Bildirimler"
              type="button"
            >
              <FiBell size={18} />
              {unread > 0 && <span className="notif-badge">{unread}</span>}
            </button>

            {notifOpen && (
              <div className="notif-popover" role="menu">
                <div className="notif-head">
                  <span>Yeni Eklenen İşler</span>
                  {notifications.length > 0 && (
                    <button className="notif-clear" onClick={clearRecent} type="button">
                      Temizle
                    </button>
                  )}
                </div>

                {notifications.length ? (
                  <ul className="notif-list">
                    {notifications.map((n) => (
                      <li key={`${n.id}-${n.created_at}`}>
                        <button
                          className="notif-item"
                          role="menuitem"
                          onClick={() => goBug(n.href, n)}
                          title={n.title}
                          type="button"
                        >
                          <span
                            className="notif-dot"
                            style={{ opacity: n.is_read ? 0.35 : 1 }}
                          />
                          <div className="notif-texts">
                            <span className="notif-title">{n.title}</span>
                            {n.projectName ? (
                              <span className="notif-sub">{n.projectName}</span>
                            ) : null}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="notif-empty">Henüz yeni iş yok</div>
                )}
              </div>
            )}
          </div>

          <button
            className="theme-btn"
            onClick={toggleTheme}
            title={isDark ? "Açık temaya geç" : "Koyu temaya geç"}
            type="button"
          >
            {isDark ? <FiSun size={18} /> : <FiMoon size={18} />}
          </button>

          <div className="menu-wrap" ref={menuRef}>
            <button
              className="menu-btn"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
              title="Menü"
              type="button"
            >
              <FiMenu size={18} />
            </button>

            {menuOpen && (
              <div className="menu-popover" role="menu">
                <button
                  className="menu-item"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    nav("/");
                  }}
                  type="button"
                >
                  <FiHome size={16} /> <span>Ana Sayfa</span>
                </button>

                <button
                  className="menu-item"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    nav("/profile");
                  }}
                  type="button"
                >
                  <FiUser size={16} /> <span>Profil</span>
                </button>

                <a
                  className="menu-item"
                  role="menuitem"
                  href="https://buglog.co/"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setMenuOpen(false)}
                >
                  <FiInfo size={16} /> <span>Hakkında</span>
                </a>

                <button
                  className="menu-item danger"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    handleLogout();
                  }}
                  type="button"
                >
                  <FiLogOut size={16} /> <span>Çıkış Yap</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
